import {
  HomePageCardHeader,
  HomePageCardTitle,
} from "components/BooksHomePage/HomePageCardHeader";
import { ConditionalLink } from "components/conditionalLink";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import Card from "components/DesignSystem/Card/Card";
import { LockedUi } from "components/LockedUi/LockedUi";
import { Padlock } from "components/icons/Padlock";
import { Financials as FinancialsIcon } from "components/icons/LeftNav/Books/Financials";
import { RightArrow } from "components/icons/RightArrow";
import {
  CHAT_REQUESTS,
  CONNECTIONS,
  DOCUMENTS_REQUESTED,
  DOCUMENT_REQUESTED,
  FINANCIAL_CLOSING,
  INFO_REQUESTED,
  NEW_ACCOUNTS_DETECTED,
} from "constants/bookkeeping";
import {
  ACCOUNTANT_ACCESS_REQUEST_CARD_TYPE,
  REQUESTED_INFO_CUSTOM_TYPE,
} from "constants/chatType";
import { INKLE_BOOKS, INKLE_TAX } from "constants/home";
import { useBookActionItems } from "hooks/useBookActionItems";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { FinancialClosing } from "types/Models/books";
import { TodoListLoader } from "pages/TodoList/TodoListLoader";
import { ReactNode, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import InfoGray from "static/images/InfoGray.svg";
import NoPendingItems from "static/images/NoPendingItems.svg";
import { openFloatingChat, setToOpenChatId } from "store/slices/chat";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import { setActiveChannelGroupId } from "store/slices/reviewAndBalancePayment";
import { BankItems } from "types/Models/books";
import { RequestedInfoCustomTypeCard } from "./RequestedInfoCustomTypeCard";
import { pluralize } from "utils/pluralize";
import { EmptyAgentItemScreen } from "components/AgentDashboard/EmptyAgentItemScreen";
import { Accountant } from "components/icons/accountant";

export const ItemCard = ({
  text,
  logo,
  action,
  description,
  handleClick,
}: {
  logo?: ReactNode;
  text: string;
  action: ReactNode;
  description: string;
  handleClick: () => void;
}) => {
  const hasWritePermission = useHasWritePermission(WRITE_PERMISSION_KEYS.HOME);

  return (
    <div
      className="t-flex t-justify-between t-py-4 t-px-2 sm:t-pl-0 t-border t-border-solid t-border-neutral-0 t-border-t-surface-transparent t-border-l-surface-transparent t-border-r-surface-transparent t-group active:t-border-purple active:t-border-t-purple active:t-border-r-purple active:t-border-l-purple t-rounded-lg hover:t-bg-surface-lighter-grey"
      role="button"
      onClick={hasWritePermission ? handleClick : undefined}
    >
      <div className="t-flex t-min-w-0 t-gap-2 t-items-center sm:t-items-start sm:t-gap-3">
        <div className="t-relative t-min-h-10 t-min-w-10 t-max-h-10 t-max-w-10 t-rounded-full t-flex t-items-center t-justify-center t-bg-surface-lighter-grey">
          {logo}
        </div>
        <div className="t-min-w-0">
          <div className="t-text-subtext">{text}</div>
          <div className="t-text-body-sm t-text-text-30">{description}</div>
        </div>
      </div>

      <div className="t-relative t-flex t-items-center t-justify-end sm:t-shrink-0 sm:t-pl-2">
        <LockedUi
          feature={WRITE_PERMISSION_KEYS.HOME}
          blockedUi={
            <span className="t-text-text-30">
              <Padlock />
            </span>
          }
        >
          <div className="t-text-text-60 t-opacity-100 group-hover:t-opacity-0 t-transition-opacity t-duration-150 t-absolute t-right-0 sm:t-static sm:group-hover:t-opacity-100">
            <RightArrow />
          </div>
          <div className="t-hidden t-opacity-0 t-transition-opacity t-duration-150 group-hover:t-opacity-100 md:t-block">
            {action}
          </div>
        </LockedUi>
      </div>
    </div>
  );
};

const ActionItemsCard = ({
  item,
  isGroupBased,
}: {
  item: any;
  isGroupBased: boolean;
}) => {
  const { link: constructInternalLink } = useConstructInternalLink();
  const dispatch = useDispatch();
  const entityId = useCurrentEntityId() || item.entity_id;
  const { isCustomer } = useRoleBasedView();
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const history = useHistory();

  const jumpToMsg = () => {
    if (item?.id && item?.channel_url) {
      dispatch(setScrollToMessageId(item?.id));
      dispatch(setToOpenChatId(item?.channel_url!));
      dispatch(openFloatingChat());
    }
  };

  switch (item?.item_type) {
    case NEW_ACCOUNTS_DETECTED:
      const link = constructInternalLink(
        `/books/data-sources/${item.entity_item}`
      );
      return (
        <ItemCard
          handleClick={() => history.push(link)}
          description="Connect these accounts to start syncing transactions"
          logo={<Avatar alt={CONNECTIONS} src={item?.logo} />}
          text={
            item.new_bank_accounts_count > 0
              ? `${pluralize(
                  item.items_count,
                  "new account",
                  "new accounts"
                )} found for ${item.bank_name}`
              : `New accounts found for ${item.bank_name}`
          }
          action={
            <ConditionalLink to={link}>
              <Button customType="text" size="small">
                Go to {item.bank_name}
              </Button>
            </ConditionalLink>
          }
        />
      );

    case CONNECTIONS:
      const connectionItem = item as BankItems;
      const groupBasedUrl = constructInternalLink("/books/data-sources", {
        keys: ["entity", "company"],
        includeUrl: false,
        moreQuery: {
          entity: entityId,
          ...(isCustomer ? {} : { company: groupId }),
        },
      });

      return (
        <ItemCard
          handleClick={() => history.push(groupBasedUrl)}
          description="Keep your financial data accurate by reconnecting your accounts "
          logo={<Avatar alt={CONNECTIONS} src={connectionItem?.logo} />}
          text={`${connectionItem?.bank_name} accounts are disconnected`}
          action={
            <Button customType="text" size="small">
              Reconnect
            </Button>
          }
        />
      );

    case FINANCIAL_CLOSING: {
      const closingItem = item as FinancialClosing & { item_type: string };
      const bookkeepingChannel = entities.find(
        ({ uuid }) => uuid === closingItem.entity_id
      )?.bookkeeping_channel;

      const openFinancialClosingChat = () => {
        if (!bookkeepingChannel) return;
        dispatch(setScrollToMessageId(closingItem.review_message_id));
        dispatch(setToOpenChatId(bookkeepingChannel));
        dispatch(openFloatingChat());
      };

      return (
        <ItemCard
          handleClick={openFinancialClosingChat}
          logo={<FinancialsIcon />}
          text={closingItem.closing_period}
          description="Financial closing is pending review"
          action={
            <Button
              customType="text"
              size="small"
              onClick={openFinancialClosingChat}
            >
              Review
            </Button>
          }
        />
      );
    }

    case ACCOUNTANT_ACCESS_REQUEST_CARD_TYPE: {
      const jumpToAccountantMsg = () => {
        if (item?.message_id && item?.channel_url) {
          dispatch(setScrollToMessageId(item.message_id));
          dispatch(setToOpenChatId(item.channel_url));
          dispatch(openFloatingChat());
        }
      };

      return (
        <ItemCard
          handleClick={jumpToAccountantMsg}
          logo={<Accountant />}
          text={`${item?.items_count} Pending ‘accountant access’ requests`}
          description="Please grant accountant access to our bookkeeping team so they can manage your accounts."
          action={
            <Button customType="text" size="small">
              Grant access
            </Button>
          }
        />
      );
    }

    case REQUESTED_INFO_CUSTOM_TYPE:
      return <RequestedInfoCustomTypeCard messageId={item.message_id} />;

    case CHAT_REQUESTS:
      let displayText = item?.header;
      let displayCount = item?.transaction_count;
      if (item?.header === INFO_REQUESTED) {
        displayText = `Information requested for ${item?.transaction_count} transactions`;
      }

      if (item?.header.includes(DOCUMENT_REQUESTED)) {
        displayCount = item?.text.replace(DOCUMENT_REQUESTED, "");
        displayText = DOCUMENT_REQUESTED;
      }

      if (item?.header.includes(DOCUMENTS_REQUESTED)) {
        displayCount = item?.header.replace(DOCUMENTS_REQUESTED, "");
        displayText = DOCUMENTS_REQUESTED;
      }

      return (
        <ItemCard
          handleClick={jumpToMsg}
          logo={<Avatar alt={displayCount?.toString()} src={InfoGray} />}
          text={displayText}
          description="Provide the necessary information to ensure accurate books"
          action={
            <Button customType="text" size="small">
              Add info
            </Button>
          }
        />
      );

    default:
      return null;
  }
};

export const ActionItems = ({
  isGroupBased = false,
  selectedStatusTab,
}: {
  isGroupBased?: boolean;
  selectedStatusTab: "REQUESTED" | "DEPRECATED" | "SUBMITTED";
}) => {
  const { uuid: groupId } = useCurrentGroupContext();

  const { allItems, isEmpty, isLoading, isSuccess } = useBookActionItems({
    groupId,
    card_status: selectedStatusTab,
  });

  const emptyMessage: Record<"REQUESTED" | "DEPRECATED" | "SUBMITTED", string> =
    {
      REQUESTED: "You have no pending items",
      DEPRECATED: "You have no archived action items",
      SUBMITTED: "Your completed list is currently empty",
    };

  return (
    <Async.Root
      {...{
        isEmpty,
        isLoading,
        isSuccess,
      }}
      customLoader={<TodoListLoader />}
    >
      <Async.Empty>
        <div className="t-flex t-h-64 t-w-full t-items-center t-justify-center">
          <EmptyAgentItemScreen />
        </div>
      </Async.Empty>
      <Async.Success>
        {allItems.map((item, i) => (
          <ActionItemsCard item={item} key={i} isGroupBased={isGroupBased} />
        ))}
      </Async.Success>
    </Async.Root>
  );
};

export const HomeActionItems = () => {
  const { isCustomer } = useRoleBasedView();

  return (
    <Card.Root shadow className="t-h-96 sm:t-px-4 sm:t-pb-4" borderRounded="lg">
      <HomePageCardHeader>
        <HomePageCardTitle>Books Action Centre</HomePageCardTitle>
      </HomePageCardHeader>
      <Card.Body className="sm:t-mt-2">
        <ActionItems isGroupBased={isCustomer} selectedStatusTab="REQUESTED" />
      </Card.Body>
    </Card.Root>
  );
};
